import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { useLanguage } from '../i18n/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { APP } from '../config/app';
import { listenToRideShare, listenToPublicTrack, listenToServerTimeOffset } from '../services/firebase';
import {
  createMap, destroyMap, placeMarker, fitMapToPoints, fitMapToPoint, clearMarkers,
  getDirections, drawRoute, AnimateGliderMarker, DAY_MAP_STYLE, reverseGeocode,
} from '../services/maps';
import LangToggle from '../components/LangToggle';
import RideLoader from '../components/RideLoader';
import MapRecenterIcon from '../components/MapRecenterIcon';
import { formatDriverName } from '../utils/driverName';
import {
  trackStage, liveEtaMinutes, arriveAroundMs, shareTitleName, shareTitleKeys, splitRouteAt, vehicleLabel, endedView,
  bookOwnHref,
} from '../utils/trackShare';

const isGenericPlaceName = (name) => {
  if (!name) return true;
  const s = String(name).trim().toLowerCase();
  return s === 'selected location'
    || s === 'shared location'
    || s === 'drop'
    || s === 'pickup'
    || s === 'drop location'
    || s === 'pickup location'
    || s === 'ఎంచుకున్న స్థానం'
    || /^[a-z0-9]{4}\+[a-z0-9]{2,}/i.test(s)
    || /^[+-]?\d+\.\d+,\s*[+-]?\d+\.\d+$/.test(s);
};

const HEADER_H = 64;

// 12-hour time ("2:06 pm"), which is how riders in Piler read the clock,
// whatever the viewer's browser defaults to.
const formatClock = (ms) => (ms
  ? new Date(ms).toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true })
  : '');

/**
 * The family share page (/track/:token). Public and forwardable, so it shows
 * only what rideShares/{token} + publicTracks/{token} carry while the trip is
 * live: whose ride, the moving auto, a live ETA, the plate/model/rating, and
 * 112 / MyTownly support. Never the Anna's or the rider's phone number.
 */
export default function TrackRide() {
  const { token } = useParams();
  const { t } = useLanguage();
  const { isDark } = useTheme();
  const [share, setShare] = useState(null);
  const [captainLoc, setCaptainLoc] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [ready, setReady] = useState(false);
  const [retryNonce, setRetryNonce] = useState(0);
  const [mapReady, setMapReady] = useState(false);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const [clock, setClock] = useState(() => Date.now());
  const [resolvedPickupName, setResolvedPickupName] = useState('');
  const [resolvedDropName, setResolvedDropName] = useState('');
  const [userPanned, setUserPanned] = useState(false);
  const [photoFailed, setPhotoFailed] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [routePath, setRoutePath] = useState(null);
  const [serverOffsetMs, setServerOffsetMs] = useState(0);

  const mapRef = useRef(null);
  const sheetRef = useRef(null);
  const mapInstance = useRef(null);
  const markers = useRef([]);
  const routeLines = useRef([]);
  const routeSplitRef = useRef(null);
  const routeDrawSeq = useRef(0);
  const captainMarker = useRef(null);
  const userPannedRef = useRef(false);
  const didFitRef = useRef(false);
  const fitStageRef = useRef(null);

  const stage = trackStage(share);
  const isLive = Boolean(share?.active);
  const hasShare = Boolean(share);

  // Room the map must leave for the header and the bottom sheet, so pins and
  // the auto land in the part of the map the viewer can actually see.
  const mapPadding = useCallback(() => ({
    top: HEADER_H + 28,
    bottom: Math.round(sheetRef.current?.getBoundingClientRect().height || 360) + 24,
    left: 40,
    right: 40,
  }), []);

  const frameMap = useCallback(() => {
    const map = mapInstance.current;
    if (!map || !share) return false;
    const pad = mapPadding();
    const auto = isLive && captainLoc?.lat ? { lat: captainLoc.lat, lng: captainLoc.lng } : null;
    const pickup = share.pickupApprox;
    const drop = share.dropApprox;
    // On the trip, fit the auto and the road still ahead (not just the drop
    // pin: the route can end on a road beyond it); before it, the whole route.
    const route = routePath ? splitRouteAt(routePath, stage === 'ontrip' ? auto : null) : null;
    let pts;
    if (stage === 'searching') pts = [pickup];
    else if (stage === 'arriving' || stage === 'arrived') pts = [pickup, auto];
    else if (stage === 'ontrip') pts = [auto || pickup, drop, ...(route?.ahead || [])];
    else pts = [pickup, drop, ...(route?.ahead || [])];
    pts = pts.filter((p) => p?.lat != null && p?.lng != null);
    if (!pts.length) return false;
    if (pts.length === 1) fitMapToPoint(map, pts[0], pad, 15.8);
    else fitMapToPoints(map, pts, pad, 16);
    return true;
  }, [share, captainLoc, isLive, stage, mapPadding, routePath]);

  const recenter = () => {
    userPannedRef.current = false;
    setUserPanned(false);
    frameMap();
  };

  useEffect(() => {
    if (!token) {
      setLoadError(true);
      return undefined;
    }
    return listenToRideShare(
      token,
      (data) => {
        setReady(true);
        setLastUpdatedAt(Date.now());
        setShare(data);
        if (!data) setLoadError(true);
        else setLoadError(false);
      },
      () => {
        setReady(true);
        setLoadError(true);
      },
    );
  }, [token, retryNonce]);

  // Live position (~11 m while the trip is on) plus the server's live ETA.
  useEffect(() => {
    if (!token || !share?.active) {
      setCaptainLoc(null);
      return undefined;
    }
    return listenToPublicTrack(token, (location) => {
      setCaptainLoc(location);
      setLastUpdatedAt(Date.now());
    });
  }, [token, share?.active]);

  // Server clock offset, so the server-stamped ETA ages correctly on a phone
  // whose clock is off.
  useEffect(() => {
    if (!share?.active) return undefined;
    return listenToServerTimeOffset(setServerOffsetMs);
  }, [share?.active]);

  // Dynamically resolve drop & pickup names if missing, generic, or raw plus-codes
  useEffect(() => {
    // Names are only shown during the trip; the ended view doesn't need them
    // (each lookup is a billed Geocoding call).
    if (!share?.active) return;
    const dropLat = share.dropApprox?.lat;
    const dropLng = share.dropApprox?.lng;
    const dropName = share.dropApprox?.name;
    if (dropLat != null && dropLng != null && isGenericPlaceName(dropName)) {
      reverseGeocode(dropLat, dropLng).then((place) => {
        if (place?.name && !isGenericPlaceName(place.name)) {
          setResolvedDropName(place.name);
        } else if (place?.address) {
          const firstPart = place.address.split(',')[0].trim();
          if (firstPart) setResolvedDropName(firstPart);
        }
      }).catch(() => {});
    }

    const pickupLat = share.pickupApprox?.lat;
    const pickupLng = share.pickupApprox?.lng;
    const pickupName = share.pickupApprox?.name;
    if (pickupLat != null && pickupLng != null && isGenericPlaceName(pickupName)) {
      reverseGeocode(pickupLat, pickupLng).then((place) => {
        if (place?.name && !isGenericPlaceName(place.name)) {
          setResolvedPickupName(place.name);
        } else if (place?.address) {
          const firstPart = place.address.split(',')[0].trim();
          if (firstPart) setResolvedPickupName(firstPart);
        }
      }).catch(() => {});
    }
  }, [
    share?.active,
    share?.dropApprox?.lat, share?.dropApprox?.lng, share?.dropApprox?.name,
    share?.pickupApprox?.lat, share?.pickupApprox?.lng, share?.pickupApprox?.name,
  ]);

  useEffect(() => {
    if (!share || mapInstance.current || !mapRef.current) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const center = share.pickupApprox || APP.mapCenter;
        const map = await createMap(mapRef.current, center, { night: isDark, allowNative: true });
        if (cancelled) { destroyMap(map); return; }
        mapInstance.current = map;
        // Once the viewer drags the map, stop re-framing it on every location
        // tick — they're looking at something. A recenter button brings it back.
        map.addListener('dragstart', () => {
          userPannedRef.current = true;
          setUserPanned(true);
        });
        setMapReady(true);
      } catch (e) {
        console.error(e);
      }
    })();
    return () => { cancelled = true; };
  }, [share, isDark]);

  // Once the trip has ended the page shows no map (only the result and a Book
  // button), so a map from the live view is released here.
  useEffect(() => {
    if (isLive || !mapInstance.current) return;
    try { captainMarker.current?.destroy(); } catch { /* */ }
    captainMarker.current = null;
    clearMarkers(markers.current);
    markers.current = [];
    routeLines.current.forEach((l) => { try { l?.setMap(null); } catch { /* */ } });
    routeLines.current = [];
    destroyMap(mapInstance.current);
    mapInstance.current = null;
    didFitRef.current = false;
    setMapReady(false);
  }, [isLive]);

  // A native map lives outside the page (under the WebView), so it must be
  // released when this screen closes. No-op for the JS map.
  useEffect(() => () => {
    try { captainMarker.current?.destroy(); } catch { /* */ }
    destroyMap(mapInstance.current);
    mapInstance.current = null;
  }, []);

  useEffect(() => {
    if (!mapInstance.current) return;
    mapInstance.current.setOptions({
      styles: DAY_MAP_STYLE,
    });
  }, []);

  // Pickup / drop pins: placed once, never rebuilt on a location tick.
  useEffect(() => {
    if (!share || !mapReady || !mapInstance.current) return;
    let cancelled = false;
    (async () => {
      clearMarkers(markers.current);
      markers.current = [];
      if (share.pickupApprox) {
        markers.current.push(await placeMarker(mapInstance.current, share.pickupApprox, 'A'));
      }
      if (!cancelled && share.dropApprox) {
        markers.current.push(await placeMarker(mapInstance.current, share.dropApprox, 'B'));
      }
    })();
    return () => { cancelled = true; };
  }, [
    share?.pickupApprox?.lat, share?.pickupApprox?.lng,
    share?.dropApprox?.lat, share?.dropApprox?.lng, mapReady,
  ]);

  // The auto: glides between fixes and turns with the road (same marker as the
  // rider's own trip screen). Removed when the trip ends.
  useEffect(() => {
    const map = mapInstance.current;
    if (!map || !mapReady) return;
    if (!isLive || !captainLoc?.lat || !captainLoc?.lng) {
      try { captainMarker.current?.destroy(); } catch { /* */ }
      captainMarker.current = null;
      return;
    }
    const pos = { lat: Number(captainLoc.lat), lng: Number(captainLoc.lng) };
    if (!Number.isFinite(pos.lat) || !Number.isFinite(pos.lng)) return;
    if (captainMarker.current) captainMarker.current.updatePosition(pos);
    else captainMarker.current = new AnimateGliderMarker(map, pos);
  }, [captainLoc?.lat, captainLoc?.lng, isLive, mapReady]);

  // Frame the map when it first loads, when the auto or the route first appears, and
  // whenever the trip stage changes — never on a plain location tick, and a
  // viewer's own panning is respected until the stage changes.
  const hasAuto = Boolean(isLive && captainLoc?.lat);
  useEffect(() => {
    if (!mapReady || !share) return;
    const key = `${stage}:${hasAuto ? 1 : 0}:${routePath ? 1 : 0}`;
    if (didFitRef.current && fitStageRef.current === key) return;
    const stageChanged = String(fitStageRef.current || '').split(':')[0] !== stage;
    if (stageChanged && didFitRef.current) {
      userPannedRef.current = false;
      setUserPanned(false);
    } else if (userPannedRef.current) {
      fitStageRef.current = key;
      return;
    }
    if (frameMap()) {
      didFitRef.current = true;
      fitStageRef.current = key;
    }
  }, [share, stage, hasAuto, routePath, mapReady, frameMap]);

  // The road route A→B: one Directions request per page view, kept as a path
  // so the trip can be split into "driven" (grey) and "to go" (green).
  useEffect(() => {
    const origin = share?.pickupApprox;
    const destination = share?.dropApprox;
    if (!mapReady || !origin?.lat || !origin?.lng || !destination?.lat || !destination?.lng) {
      setRoutePath(null);
      return undefined;
    }
    let cancelled = false;
    getDirections(origin, destination)
      .then((info) => { if (!cancelled && info?.polyline?.length) setRoutePath(info.polyline); })
      .catch((e) => {
        // A route failure must never invent a line; endpoint markers remain.
        console.warn('Public track route unavailable:', e?.message || e);
      });
    return () => { cancelled = true; };
  }, [share?.pickupApprox?.lat, share?.pickupApprox?.lng, share?.dropApprox?.lat, share?.dropApprox?.lng, mapReady]);

  // Draw the route: whole line green before the trip starts; once it starts,
  // grey behind the auto and green ahead. Redrawn only when the split point
  // moves to another vertex, not on every tick.
  useEffect(() => {
    const map = mapInstance.current;
    const clear = () => {
      routeLines.current.forEach((l) => { try { l?.setMap(null); } catch { /* */ } });
      routeLines.current = [];
    };
    if (!map || !mapReady || !routePath) {
      clear();
      routeSplitRef.current = null;
      return undefined;
    }
    const split = stage === 'ontrip' && captainLoc?.lat
      ? splitRouteAt(routePath, captainLoc)
      : { done: [], ahead: routePath, index: -1 };
    const key = `${stage === 'ontrip' ? 'trip' : 'plan'}:${split.index}`;
    if (routeSplitRef.current === key) return;
    routeSplitRef.current = key;
    // Only the newest draw is kept. (A cleanup flag would also throw away a
    // draw still in flight on the native map when the next fix arrives, and
    // leave the old split on screen.)
    const seq = ++routeDrawSeq.current;
    (async () => {
      const lines = [];
      if (split.done.length > 1) lines.push(await drawRoute(map, split.done, { muted: true }));
      if (split.ahead.length > 1) lines.push(await drawRoute(map, split.ahead));
      if (seq !== routeDrawSeq.current || mapInstance.current !== map) {
        lines.forEach((l) => { try { l?.setMap(null); } catch { /* */ } });
        return;
      }
      clear();
      routeLines.current = lines;
    })().catch(() => {});
  }, [routePath, stage, captainLoc?.lat, captainLoc?.lng, mapReady]);

  useEffect(() => () => {
    routeLines.current.forEach((l) => { try { l?.setMap(null); } catch { /* */ } });
    routeLines.current = [];
  }, []);

  // Keeps the recenter button just above the sheet, whatever its height.
  useEffect(() => {
    const el = sheetRef.current;
    const screen = el?.closest('.track-screen');
    if (!el || !screen || typeof ResizeObserver === 'undefined') return undefined;
    const sync = () => screen.style.setProperty('--track-sheet-h', `${Math.round(el.getBoundingClientRect().height)}px`);
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ready, loadError, hasShare]);

  // Ticks the "updated" label and the ETA countdown between server updates.
  useEffect(() => {
    if (!isLive) return undefined;
    const id = window.setInterval(() => setClock(Date.now()), 10000);
    return () => window.clearInterval(id);
  }, [isLive]);

  const updatedSeconds = lastUpdatedAt ? Math.max(0, Math.floor((clock - lastUpdatedAt) / 1000)) : null;
  const updatedLabel = updatedSeconds == null
    ? ''
    : updatedSeconds < 10
      ? (t('track.updatedJustNow') || 'Updated just now')
      : (t('track.updatedAgo', { seconds: updatedSeconds }) || `Updated ${updatedSeconds}s ago`);

  const retryTracking = () => {
    setReady(false);
    setLoadError(false);
    setShare(null);
    setCaptainLoc(null);
    setLastUpdatedAt(null);
    setRetryNonce((n) => n + 1);
  };

  if (!ready) {
    return (
      <RideLoader
        className="track-loader"
        title={t('track.title') || 'Live trip'}
        subtitle={t('track.loading') || 'Connecting to the latest trip location…'}
      />
    );
  }

  if (loadError || !share) {
    return (
      <div className="rapido-screen rapido-loading-screen track-ended">
        <div className="rapido-loading-auto">🛺</div>
        <h2>{t('track.notFound')}</h2>
        <p className="text-muted">{t('track.notFoundSub')}</p>
        <div className="track-error-actions">
          <button type="button" className="btn btn-primary" onClick={retryTracking}>{t('track.retry') || 'Try again'}</button>
          <Link to="/" className="btn btn-outline">{t('track.openApp')}</Link>
        </div>
      </div>
    );
  }

  // A link whose trip ended over 12 hours ago looks the same as a deleted one
  // (the server removes it), even before the clean-up has run.
  const ended = endedView(share, clock);
  if (ended?.kind === 'expired') {
    return (
      <div className="rapido-screen rapido-loading-screen track-ended">
        <div className="rapido-loading-auto">🛺</div>
        <h2>{t('track.expiredTitle')}</h2>
        <p className="text-muted">{t('track.expiredSub')}</p>
        <div className="track-error-actions">
          <Link to="/" className="btn btn-primary">{t('track.openApp')}</Link>
        </div>
      </div>
    );
  }

  const rawDriverName = String(share.driverName || '').trim();
  const captainName = formatDriverName(rawDriverName);
  const initial = rawDriverName.replace(/^(Anna|Captain)\s+/i, '').trim().split(/\s+/)[0]?.charAt(0).toUpperCase() || 'A';
  const plate = String(share.vehicleNumber || '').trim().replace(/^#+\s*/, '');
  const vehicle = vehicleLabel(share.vehicleModel);
  const rating = Number(share.driverRatingAvg);
  const hasRating = Number.isFinite(rating) && rating >= 1 && rating <= 5;
  const photoUrl = String(share.driverPhoto || '').trim();
  const photo = photoUrl && !photoFailed ? photoUrl : '';
  const riderName = shareTitleName(share.riderFirstName);
  const titleKeys = shareTitleKeys(share);
  const pickupName = resolvedPickupName || share.pickupApprox?.name || t('track.pickup');
  const dropName = resolvedDropName
    || (isGenericPlaceName(share.dropApprox?.name) ? '' : share.dropApprox?.name)
    || t('track.drop');
  const hasAnna = isLive && (captainName || plate || photo);

  const etaMin = liveEtaMinutes(captainLoc, stage, clock + serverOffsetMs);
  const arriveAt = formatClock(arriveAroundMs(etaMin, clock));
  const etaPill = etaMin != null ? t('track.minutes', { n: etaMin }) : '';
  const stageCopy = {
    searching: { title: t('track.stageSearching'), sub: t('track.stageSearchingSub') },
    arriving: {
      title: etaMin != null ? t('track.stageArriving') : t('track.stageArrivingNoEta'),
      sub: etaMin != null ? t('track.arrivePickupAround', { time: arriveAt }) : t('track.stageArrivingSub'),
    },
    arrived: { title: t('track.stageArrived'), sub: t('track.stageArrivedSub') },
    ontrip: {
      title: etaMin != null ? t('track.stageOnTrip') : t('track.stageOnTripNoEta'),
      sub: etaMin != null ? t('track.arriveAround', { time: arriveAt }) : t('track.stageOnTripSub'),
    },
  }[stage];

  const header = (
    <header className="track-header">
      <span className="track-header-mark" aria-hidden>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 21s-6.5-6.2-6.5-11a6.5 6.5 0 0 1 13 0c0 4.8-6.5 11-6.5 11z" />
          <circle cx="12" cy="10" r="2.3" />
        </svg>
      </span>
      <div className="track-header-text">
        <h1>{riderName ? t(titleKeys.named, { name: riderName }) : t(titleKeys.anon)}</h1>
        <span>{t('track.sharedFrom', { app: APP.name, city: APP.city })}</span>
      </div>
      <span className={`track-live-chip${isLive ? ' is-live' : ''}`}>
        <span className="track-live-chip-dot" aria-hidden />
        {isLive ? t('track.live') : t('track.ended')}
      </span>
      <LangToggle />
    </header>
  );

  // After the trip: just the result and a Book button. No map (nothing left
  // to show on it, and on the website every map load is billed), and no
  // notes about the link expiring or the Anna's details being hidden.
  if (ended) {
    const done = ended.kind === 'completed';
    const title = done
      ? (ended.endedMs ? t('track.completedAt', { time: formatClock(ended.endedMs) }) : t('track.stageEnded'))
      : (ended.endedMs ? t('track.cancelledAt', { time: formatClock(ended.endedMs) }) : t('track.stageCancelled'));
    return (
      <div className="rapido-screen track-screen track-done-screen">
        {header}
        <div className="track-done">
          <div className="track-done-result" aria-live="polite">
            <span className={`track-done-icon${done ? '' : ' is-cancelled'}`} aria-hidden>
              {done ? (
                <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12.5l4.5 4.5L19 7.5" />
                </svg>
              ) : (
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
                  <path d="M7 7l10 10M17 7L7 17" />
                </svg>
              )}
            </span>
            <h2>{title}</h2>
            <p>{done ? t('track.doneThanks') : t('track.cancelledNote')}</p>
          </div>
          <div className="track-done-cta">
            <p>{t('track.needAuto', { city: APP.city })}</p>
            {Capacitor.isNativePlatform() ? (
              // Already in our app: go home without reloading the page.
              <Link to={bookOwnHref({ isNative: true })} className="btn btn-primary track-ended-cta">
                {t('track.bookOwn', { city: APP.city })}
              </Link>
            ) : (
              // A full page load on purpose: /app is served by app.html
              // (firebase.json), which sends Android to the Play Store.
              <a href={bookOwnHref()} className="btn btn-primary track-ended-cta">
                {t('track.bookOwn', { city: APP.city })}
              </a>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rapido-screen track-screen">
      {header}

      <div ref={mapRef} className="rapido-map" />

      {userPanned && (
        <button
          type="button"
          className="rapido-recenter-fab track-recenter"
          onClick={recenter}
          aria-label={t('active.recenterMap') || 'Recenter map'}
        >
          <MapRecenterIcon />
        </button>
      )}

      <section ref={sheetRef} className="track-sheet" aria-label={t('track.title') || 'Live trip'}>
        <div className="rapido-sheet-handle" aria-hidden />
        <div className="track-sheet-body">
          <div className="track-eta-row" aria-live="polite">
            <div className="track-eta-text">
              <h2>{stageCopy.title}</h2>
              <span>{stageCopy.sub}</span>
            </div>
            {etaPill && <span className="track-eta-pill">{etaPill}</span>}
          </div>

          {hasAnna && (
            <div className="track-anna-card">
              <div className="track-anna-info">
                {plate && <span className="track-anna-plate">{plate}</span>}
                <span className="track-anna-vehicle">
                  {vehicle.kind === 'type' && t('track.vehicleType')}
                  {vehicle.kind === 'model' && vehicle.model}
                  {vehicle.kind === 'typeAndModel' && t('track.vehicleWithModel', { model: vehicle.model })}
                </span>
                {captainName && (
                  <span className="track-anna-name">
                    {captainName}
                    <svg width="15" height="15" viewBox="0 0 24 24" role="img" aria-label={t('track.verified')}>
                      <circle cx="12" cy="12" r="10" fill="#00A86B" />
                      <path d="M7.5 12.5l3 3 6-6.5" fill="none" stroke="#FFFFFF" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                )}
              </div>
              <div className="track-anna-photo-wrap">
                {photo ? (
                  <button
                    type="button"
                    className="track-captain-photo-btn"
                    onClick={() => setPhotoPreview(true)}
                    aria-label={t('track.viewPhoto') || 'View photo'}
                  >
                    <img
                      className="track-captain-photo"
                      src={photo}
                      alt={captainName || 'Anna'}
                      onError={() => setPhotoFailed(true)}
                    />
                  </button>
                ) : (
                  <div className="track-captain-avatar" aria-hidden>{initial}</div>
                )}
                {hasRating && (
                  <span className="track-anna-rating" aria-label={t('track.ratingAria', { rating: rating.toFixed(1) })}>
                    {rating.toFixed(1)}
                    <svg width="11" height="11" viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M12 2.8l2.8 6 6.4.6-4.8 4.3 1.4 6.4L12 16.9 6.2 20.1l1.4-6.4-4.8-4.3 6.4-.6z" fill="#E8A317" />
                    </svg>
                  </span>
                )}
              </div>
            </div>
          )}

          {isLive && (
            <button type="button" className="track-help-btn" onClick={() => setHelpOpen(true)}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M12 3l7 3v5c0 4.5-3 8.3-7 10-4-1.7-7-5.5-7-10V6z" />
                <path d="M12 8v4M12 15.5v.5" />
              </svg>
              {t('track.safetyHelp')}
            </button>
          )}

          {isLive && (
            <div className="track-drop-row">
              <div className="track-drop-text">
                <span>{t('track.dropAt')}</span>
                <strong>{dropName}</strong>
              </div>
              <button
                type="button"
                className="track-details-btn"
                aria-expanded={detailsOpen}
                onClick={() => setDetailsOpen((v) => !v)}
              >
                {detailsOpen ? t('track.hideDetails') : t('track.tripDetails')}
              </button>
            </div>
          )}

          {isLive && detailsOpen && (
            <div className="track-details">
              <div className="track-details-stop">
                <span className="track-dot pickup" aria-hidden />
                <div><span>{t('track.pickup')}</span><strong>{pickupName}</strong></div>
              </div>
              <div className="track-details-stop">
                <span className="track-dot drop" aria-hidden />
                <div><span>{t('track.drop')}</span><strong>{dropName}</strong></div>
              </div>
              <div className="track-details-grid">
                {share.durationText && (
                  <div><span>{t('track.tripTime')}</span><strong>{share.durationText}</strong></div>
                )}
                {arriveAt && (
                  <div>
                    <span>{stage === 'ontrip' ? t('track.arriveBy') : t('track.pickupBy')}</span>
                    <strong>{arriveAt}</strong>
                  </div>
                )}
              </div>
              <span className="track-details-privacy">{t('track.privacy')}</span>
            </div>
          )}

          {isLive && updatedLabel && <span className="track-updated-note">{updatedLabel}</span>}
        </div>
      </section>

      {helpOpen && isLive && (
        <div className="track-help-backdrop" onClick={() => setHelpOpen(false)}>
          <div
            className="track-help-sheet"
            role="dialog"
            aria-modal="true"
            aria-label={t('track.safetyHelp')}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="track-help-head">
              <h2>{t('track.safetyHelp')}</h2>
              <button type="button" className="track-help-close" onClick={() => setHelpOpen(false)} aria-label={t('common.close') || 'Close'}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
              </button>
            </div>
            <p>{riderName ? t('track.helpBodyNamed', { name: riderName }) : t('track.helpBody')}</p>
            <a href="tel:112" className="track-help-call is-emergency">{t('track.call112')}</a>
            <a href={`tel:+91${String(APP.supportPhone || '').replace(/\D/g, '').slice(-10)}`} className="track-help-call">{t('track.callSupport')}</a>
          </div>
        </div>
      )}

      {photoPreview && photo && (
        <div
          className="track-photo-lightbox"
          role="dialog"
          aria-modal="true"
          onClick={() => setPhotoPreview(false)}
        >
          <img src={photo} alt={captainName || 'Anna'} onError={() => { setPhotoFailed(true); setPhotoPreview(false); }} />
          <button
            type="button"
            className="track-photo-lightbox-close"
            onClick={() => setPhotoPreview(false)}
            aria-label={t('common.close') || 'Close'}
          >
            ✕
          </button>
        </div>
      )}
    </div>
  );
}
